import { useState, useEffect } from 'react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { apiService } from '../../services/api';
import { Link } from 'react-router-dom';
import { FileText, Clock, CheckCircle, XCircle, LogOut } from 'lucide-react';

interface DashboardData {
  client: { name: string; email: string };
  applications: any[];
  leaseApplications: any[];
  unreadCounts: Record<string, number>;
  recentMessages: any[];
  stats: { totalApplications: number; underReview: number; approved: number; rejected: number };
}

const statusColors: Record<string, string> = {
  under_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-primary-light text-primary',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-stone-100 text-stone-600',
  submitted: 'bg-blue-100 text-blue-800'
};

function getStatusText(s: string) {
  return s.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function ClientDashboard() {
  const { client, logout } = useClientAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const res = await apiService.getClientDashboard();
      if (res.status === 'success') setData(res.data);
      else setError('Failed to load dashboard');
    } catch {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-ink-muted">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-ink mb-4">{error}</p>
          <button
            type="button"
            onClick={fetchData}
            className="h-11 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const allApps = [...(data?.applications || []), ...(data?.leaseApplications || [])];

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-ink text-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold">Client Portal</h1>
              <p className="text-stone-400 text-sm mt-0.5">Welcome, {client?.name}</p>
            </div>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white/10 text-white hover:bg-white/20 font-medium"
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: data?.stats.totalApplications ?? 0, icon: FileText },
            { label: 'Under review', value: data?.stats.underReview ?? 0, icon: Clock },
            { label: 'Approved', value: data?.stats.approved ?? 0, icon: CheckCircle },
            { label: 'Rejected', value: data?.stats.rejected ?? 0, icon: XCircle }
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl bg-surface-raised shadow-card p-5 flex items-center gap-4"
            >
              <div className="w-11 h-11 rounded-xl bg-primary-light text-primary flex items-center justify-center">
                <stat.icon size={22} />
              </div>
              <div>
                <p className="text-2xl font-bold text-ink">{stat.value}</p>
                <p className="text-sm text-ink-muted">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-surface-raised shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="text-lg font-semibold text-ink">Your applications</h2>
          </div>
          <div className="divide-y divide-stone-200">
            {allApps.length === 0 ? (
              <div className="px-6 py-10 text-center text-ink-muted">
                No applications found. Contact support if this is an error.
              </div>
            ) : (
              allApps.map((app) => (
                <div key={app._id} className="px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink">
                          {app.applicantName || app.Consent?.ApplicantName}
                        </span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            statusColors[app.status] ?? 'bg-stone-100 text-stone-700'
                          }`}
                        >
                          {getStatusText(app.status)}
                        </span>
                        {data?.unreadCounts[app._id] > 0 && (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-light text-primary">
                            {data.unreadCounts[app._id]} new
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-ink-muted mt-1">
                        Shop {app.shopNumber || 'N/A'} · {app.companyName || app.Company?.CompanyName || 'N/A'} ·{' '}
                        {new Date(app.submittedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Link
                        to={`/client/applications/${app._id}`}
                        className="h-9 px-4 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-dark inline-flex items-center justify-center"
                      >
                        Details
                      </Link>
                      <Link
                        to={`/client/applications/${app._id}/messages`}
                        className="h-9 px-4 rounded-xl border border-stone-300 text-ink text-sm font-medium hover:bg-surface-muted inline-flex items-center justify-center"
                      >
                        Messages
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {data?.recentMessages && data.recentMessages.length > 0 && (
          <div className="mt-8 rounded-2xl bg-surface-raised shadow-card overflow-hidden">
            <div className="px-6 py-4 border-b border-stone-200">
              <h2 className="text-lg font-semibold text-ink">Recent messages</h2>
            </div>
            <div className="divide-y divide-stone-200">
              {data.recentMessages.map((msg: any) => (
                <div key={msg._id} className="px-6 py-4 flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-surface-muted flex items-center justify-center text-sm font-medium text-ink shrink-0">
                    {msg.senderType === 'admin' ? 'A' : 'C'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">
                      {msg.senderType === 'admin' ? 'Admin' : 'You'}
                    </p>
                    <p className="text-sm text-ink-muted truncate">{msg.content}</p>
                    <p className="text-xs text-ink-subtle mt-0.5">
                      {new Date(msg.sentAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
